import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { RiMapPinLine, RiCompass3Line, RiLoader4Line } from 'react-icons/ri';
import { API_BASE_URL } from '../config';

const TravelSection = () => {
    const [journeys, setJourneys] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchJourneys = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/travel/places/read.php`);
                const data = await response.json();
                if (response.ok) {
                    // Take the first 4 latest places
                    setJourneys((data.records || []).slice(0, 4));
                }
            } catch (error) {
                console.error('Error fetching travel sections:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchJourneys();
    }, []);

    return (
        <section className="py-24 bg-white" id="travel">
            <div className="mx-auto max-w-7xl px-6">
                <div className="flex flex-col lg:flex-row justify-between items-center lg:items-end gap-10 mb-20 text-center lg:text-left">
                    <div className="max-w-2xl">
                        <motion.span
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.4em] mb-3 block"
                        >
                            Curated Expeditions
                        </motion.span>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-8 font-display leading-[1.1]">
                            Travel with <span className="text-emerald-600 italic">Purpose.</span>
                        </h2>
                        <p className="text-lg text-slate-500 max-w-xl mx-auto lg:mx-0 font-medium leading-relaxed italic">
                            "Explore the island's most critical biodiversity corridors while contributing directly to habitat preservation and species protection."
                        </p>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/travel')}
                        className="px-10 py-5 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-slate-950/20 hover:bg-emerald-600 transition-all"
                    >
                        Explore the Map
                    </motion.button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {loading ? (
                        <div className="col-span-full flex justify-center py-20">
                            <RiLoader4Line className="animate-spin text-emerald-500 text-4xl" />
                        </div>
                    ) : (
                        journeys.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: index * 0.1 }}
                                className="group relative h-[400px] overflow-hidden rounded-[20px] shadow-xl cursor-pointer"
                                onClick={() => navigate(`/travel/place/${item.id}`)}
                            >
                                <img
                                    src={item.image_url}
                                    alt={item.name}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/10 to-transparent opacity-90 group-hover:opacity-70 transition-opacity" />

                                <div className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full border border-white/20">
                                    <RiMapPinLine size={14} className="text-emerald-400" />
                                    <span className="text-[10px] font-black text-white uppercase tracking-widest">{item.district_name}</span>
                                </div>

                                <div className="absolute bottom-0 left-0 p-8 w-full">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                                        <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">{item.category_name}</span>
                                    </div>
                                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors tracking-tight">
                                        {item.name}
                                    </h3>
                                    <p className="text-slate-300 text-xs leading-relaxed mb-6 font-medium line-clamp-3">
                                        {item.description}
                                    </p>
                                    <button className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.25em] text-white hover:text-emerald-400 transition-all">
                                        <RiCompass3Line size={18} className="text-emerald-500" />
                                        <span>Explore</span>
                                    </button>
                                </div>
                            </motion.div>
                        ))
                    )}
                </div>
            </div>
        </section>
    );
};

export default TravelSection;
